import { useState, useEffect, useMemo } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  useWorkOrders,
  useServiceSummary,
  useCreateWorkOrder,
  useSchedules,
  useReminders,
  useRunReminderJob,
  useCreateSchedule,
} from '@/features/service/hooks'
import { useBoats } from '@/features/inventory/hooks'
import { useDebounce } from '@/components/hooks/use-debounce'
import { WorkOrderForm } from '@/features/service/work-order-form'
import { useDirtyClose } from '@/hooks/use-dirty-close'
import { ScheduleForm } from '@/features/service/schedule-form'
import { ScheduleList } from '@/features/service/schedule-list'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Wrench,
  Ship,
  Plus,
  AlertTriangle,
  CalendarClock,
  CalendarDays,
  Search,
  RefreshCw,
  BellRing,
  Play,
  BookOpen,
  Package,
  ShoppingCart,
} from 'lucide-react'
import type { WorkOrderStatus, WorkOrderPriority, WorkOrderWriteBody } from '@/types/service'
import { WORK_ORDER_STATUS_LABELS } from '@/types/service'
import { requireFlow } from '@/features/auth/guards'

const SECTIONS = ['orders', 'schedules', 'reminders'] as const
type ServiceSection = (typeof SECTIONS)[number]

const STATUS_FILTERS = ['all', 'pending', 'in_progress', 'on_hold', 'completed', 'canceled'] as const
type StatusFilter = (typeof STATUS_FILTERS)[number]

function isSection(v: unknown): v is ServiceSection {
  return typeof v === 'string' && (SECTIONS as readonly string[]).includes(v)
}

function isStatusFilter(v: unknown): v is StatusFilter {
  return typeof v === 'string' && (STATUS_FILTERS as readonly string[]).includes(v)
}

export const Route = createFileRoute('/_authenticated/service/')({
  beforeLoad: requireFlow('service'),
  // Section tab, status filter, and search live in the URL so refresh/share
  // restores the exact view.
  validateSearch: (search: Record<string, unknown>): { section?: ServiceSection; status?: StatusFilter; q?: string } => ({
    section: isSection(search.section) ? search.section : undefined,
    status: isStatusFilter(search.status) ? search.status : undefined,
    q: typeof search.q === 'string' ? search.q : undefined,
  }),
  component: ServiceListPage,
})

const statusVariantMap: Record<
  WorkOrderStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  pending: 'outline',
  in_progress: 'secondary',
  on_hold: 'outline',
  completed: 'default',
  canceled: 'destructive',
}

const priorityVariantMap: Record<
  WorkOrderPriority,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  low: 'outline',
  medium: 'secondary',
  high: 'default',
  urgent: 'destructive',
}

function ServiceListPage() {
  const navigate = useNavigate({ from: '/service/' })
  const { q: urlQ, section: urlSection, status: urlStatus } = Route.useSearch()
  const [createOpen, setCreateOpen] = useState(false)
  const [formDirty, setFormDirty] = useState(false)
  const { guardOpenChange: guardCreateOpen, discardGuard: createDirtyGuard } = useDirtyClose(
    createOpen,
    setCreateOpen,
    formDirty,
  )
  const [createScheduleOpen, setCreateScheduleOpen] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const activeTab: StatusFilter = urlStatus ?? 'all'
  const section: ServiceSection = urlSection ?? 'orders'
  const [searchInput, setSearchInput] = useState(urlQ ?? '')
  const debouncedQ = useDebounce(searchInput, 300)

  useEffect(() => {
    if ((urlQ ?? '') !== debouncedQ) {
      navigate({
        search: (prev) => ({ ...prev, q: debouncedQ || undefined }),
        replace: true,
      })
    }
  }, [debouncedQ, urlQ, navigate])

  const setSection = (next: string) => {
    if (!isSection(next)) return
    navigate({ search: (prev) => ({ ...prev, section: next === 'orders' ? undefined : next }) })
  }

  const setActiveTab = (next: string) => {
    if (!isStatusFilter(next)) return
    navigate({
      search: (prev) => ({ ...prev, status: next === 'all' ? undefined : next }),
    })
  }

  const { data: summary, isLoading: summaryLoading } = useServiceSummary()
  const { data: schedules = [], isLoading: schedulesLoading } = useSchedules()
  const { data: reminders = [], isLoading: remindersLoading } = useReminders()
  const { data: boats = [] } = useBoats()
  const runReminderJobMutation = useRunReminderJob()
  const createScheduleMutation = useCreateSchedule()

  const boatNames = useMemo(() => {
    const map: Record<string, string> = {}
    for (const b of boats) map[b.id] = `${b.make} ${b.model}`
    return map
  }, [boats])

  const filters: Record<string, string> = {}
  if (debouncedQ) filters.q = debouncedQ
  if (activeTab !== 'all') filters.status = activeTab
  const {
    data: workOrders,
    isLoading,
    isError,
  } = useWorkOrders(Object.keys(filters).length > 0 ? filters : undefined)

  // The shared search filters schedules/reminders client-side (small lists).
  const sectionNeedle = debouncedQ.trim().toLowerCase()
  const visibleSchedules = sectionNeedle
    ? schedules.filter(
        (sc) =>
          sc.title.toLowerCase().includes(sectionNeedle) ||
          (boatNames[sc.boat_id] ?? '').toLowerCase().includes(sectionNeedle),
      )
    : schedules
  const visibleReminders = sectionNeedle
    ? reminders.filter(
        (r) =>
          r.schedule_title.toLowerCase().includes(sectionNeedle) ||
          r.boat_name.toLowerCase().includes(sectionNeedle) ||
          (r.customer_name ?? '').toLowerCase().includes(sectionNeedle),
      )
    : reminders

  const createMutation = useCreateWorkOrder()

  const handleCreate = (values: WorkOrderWriteBody) => {
    setCreateError(null)
    createMutation.mutate(values, {
      onSuccess: () => setCreateOpen(false),
      onError: (err) =>
        setCreateError(err instanceof Error ? err.message : 'Failed to create work order'),
    })
  }

  const reminderStatusVariant: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> =
    {
      sent: 'default',
      skipped: 'outline',
      failed: 'destructive',
    }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Wrench className="h-8 w-8" /> Service
          </h1>
          <p className="text-muted-foreground">
            Track boat servicing, repairs, and recurring schedules.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative w-full sm:w-60">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={
                section === 'reminders'
                  ? 'Search reminders…'
                  : section === 'schedules'
                    ? 'Search schedules…'
                    : 'Search work orders…'
              }
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-8"
            />
          </div>
          <Button variant="outline" asChild>
            <Link to="/service/calendar">
              <CalendarDays className="mr-2 h-4 w-4" /> Calendar
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/service/reference">
              <BookOpen className="mr-2 h-4 w-4" /> Service Reference
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/service/parts">
              <Package className="mr-2 h-4 w-4" /> Parts Catalog
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/service/purchase-orders">
              <ShoppingCart className="mr-2 h-4 w-4" /> Purchase Orders
            </Link>
          </Button>
          {section === 'orders' && (
            <Dialog
              open={createOpen}
              onOpenChange={(open) => {
                guardCreateOpen(open)
                if (!open) setCreateError(null)
              }}
            >
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" /> New Work Order
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>New Work Order</DialogTitle>
                </DialogHeader>
                <WorkOrderForm
                  onDirtyChange={setFormDirty}
                  onSubmit={handleCreate}
                  isPending={createMutation.isPending}
                />
                {createError && <p className="text-sm text-destructive">{createError}</p>}
                {createDirtyGuard}
              </DialogContent>
            </Dialog>
          )}
          {section === 'schedules' && (
            <Dialog open={createScheduleOpen} onOpenChange={setCreateScheduleOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" /> New Schedule
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>New Service Schedule</DialogTitle>
                  <DialogDescription>
                    A repeating service task tracked by calendar time and/or engine hours.
                  </DialogDescription>
                </DialogHeader>
                <ScheduleForm
                  onSubmit={(body) =>
                    createScheduleMutation.mutate(body, {
                      onSuccess: () => setCreateScheduleOpen(false),
                    })
                  }
                  isPending={createScheduleMutation.isPending}
                />
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {summaryLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Under Service
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.boats_in_service}</div>
              <p className="text-xs text-muted-foreground">boats with open work orders</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Open Work Orders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.open_work_orders}</div>
              <p className="text-xs text-muted-foreground">Pending / In Progress / On Hold</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Due Soon (7 days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.due_within_7_days}</div>
              <p className="text-xs text-muted-foreground">schedules approaching due date</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Overdue</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold flex items-center gap-2">
                {summary.overdue}
                {summary.overdue > 0 && <AlertTriangle className="h-5 w-5 text-destructive" />}
              </div>
              <p className="text-xs text-muted-foreground">past due date</p>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* Section switch + Work Order Table with Filter Tabs */}
      <Tabs value={section} onValueChange={setSection}>
        <TabsList>
          <TabsTrigger value="orders" className="gap-1.5">
            <Wrench className="h-3.5 w-3.5" /> Work Orders
          </TabsTrigger>
          <TabsTrigger value="schedules" className="gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" /> Schedules
          </TabsTrigger>
          <TabsTrigger value="reminders" className="gap-1.5">
            <BellRing className="h-3.5 w-3.5" /> Reminders
          </TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="pending">Pending</TabsTrigger>
              <TabsTrigger value="in_progress">In Progress</TabsTrigger>
              <TabsTrigger value="on_hold">On Hold</TabsTrigger>
              <TabsTrigger value="completed">Completed</TabsTrigger>
              <TabsTrigger value="canceled">Canceled</TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab}>
              {isLoading ? (
                <div className="flex flex-col gap-2 mt-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-14 w-full" />
                  ))}
                </div>
              ) : isError ? (
                <div className="text-destructive mt-4">Failed to load work orders.</div>
              ) : !workOrders?.length ? (
                <div className="text-muted-foreground text-center py-12">No work orders found.</div>
              ) : (
                <div className="mt-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Boat</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Technician</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {workOrders.map((wo) => (
                        <TableRow key={wo.id}>
                          <TableCell className="font-medium">{wo.title}</TableCell>
                          <TableCell>
                            <Link
                              to="/inventory/$boatId"
                              params={{ boatId: wo.boat_id }}
                              className="flex items-center gap-1.5 text-muted-foreground hover:underline"
                            >
                              <Ship className="h-3.5 w-3.5" />
                              {wo.boat_name}
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusVariantMap[wo.status]} className="text-xs">
                              {WORK_ORDER_STATUS_LABELS[wo.status]}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={priorityVariantMap[wo.priority]}
                              className="capitalize text-xs"
                            >
                              {wo.priority}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {wo.assigned_technician}
                          </TableCell>
                          <TableCell>
                            {wo.due_date ? (
                              <span className="flex items-center gap-1.5 text-xs">
                                <CalendarClock className="h-3.5 w-3.5" />
                                {new Date(wo.due_date).toLocaleDateString()}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Link to="/service/$workOrderId" params={{ workOrderId: wo.id }}>
                              <Button variant="outline" size="sm">
                                View
                              </Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="schedules" className="mt-4">
          <ScheduleList
            schedules={visibleSchedules}
            isLoading={schedulesLoading}
            showBoat
            boatNames={boatNames}
            emptyMessage={
              sectionNeedle
                ? 'No schedules match your search.'
                : 'No recurring schedules yet — open a boat and apply a manufacturer plan.'
            }
          />
        </TabsContent>

        <TabsContent value="reminders" className="mt-4">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              Owner texts sent when tasks enter their reminder window. Runs hourly; each due cycle
              texts once.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              disabled={runReminderJobMutation.isPending}
              onClick={() => runReminderJobMutation.mutate()}
            >
              <Play className="h-3.5 w-3.5" />
              {runReminderJobMutation.isPending
                ? 'Scanning…'
                : runReminderJobMutation.data
                  ? `Scanned ${runReminderJobMutation.data.scanned} · sent ${runReminderJobMutation.data.sent}`
                  : 'Run scan now'}
            </Button>
          </div>
          {remindersLoading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : visibleReminders.length === 0 ? (
            <div className="text-muted-foreground text-center py-12">
              {sectionNeedle ? 'No reminders match your search.' : 'No reminders sent yet.'}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sent</TableHead>
                    <TableHead>Boat</TableHead>
                    <TableHead>Task</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleReminders.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Link
                          to="/inventory/$boatId"
                          params={{ boatId: r.boat_id }}
                          className="hover:underline"
                        >
                          {r.boat_name}
                        </Link>
                      </TableCell>
                      <TableCell>{r.schedule_title}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {r.customer_name || '—'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs">
                        {r.due_kind === 'hours' ? `@ ${r.due_marker} hrs` : r.due_marker}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={reminderStatusVariant[r.status] ?? 'outline'}
                          className="capitalize"
                        >
                          {r.status}
                        </Badge>
                        {r.work_order_id && (
                          <Link
                            to="/service/$workOrderId"
                            params={{ workOrderId: r.work_order_id }}
                            className="ml-2 text-xs underline text-muted-foreground"
                          >
                            Work Order
                          </Link>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
